import { LogManager, StringUtil } from "@mcleod/core";
import { DataSourceAction } from "../../databinding/DataSource";
import { DataSourceExecutionEvent } from "../../events/DataSourceExecutionEvent";
import { Decorator } from "./Decorator";
import { DecoratorProps } from "./DecoratorProps";
import { ErrorParam } from "@mcleod/core/src/ErrorHandler";
import { HorizontalSpacer } from "../HorizontalSpacer";
import { LayoutProvidedPreviewDecoratorProps } from "./LayoutProvidedPreviewDecoratorProps";
import { MultiButton } from "../../components/multibutton/MultiButton";
import { Panel } from "../../components/panel/Panel";
import { ReflectiveDialogs } from "../../base/ReflectiveDialogs";
import { ReportPreview } from "./ReportPreview";
import { ReportPreviewDataResolver } from "./ReportPreviewDataResolver";
import { ReportPreviewTabSource } from "./ReportPreviewTabSource";
import { ResetFormButton } from "../ResetFormButton";

const log = LogManager.getLogger("components.page.PreviewDecorator");

/**
 * Base decorator class for preview-based decorators like ReportDecorator and TaskDecorator.
 * This class provides common functionality for previewing data before execution.
 */
export abstract class PreviewDecorator extends Decorator implements DecoratorProps {
    protected panelLeft: Panel;
    protected panelButtons: Panel;
    protected panelPreview: ReportPreview;
    protected buttonPreviewAction: MultiButton;
    protected buttonResetForm: ResetFormButton;

    constructor(props?: Partial<DecoratorProps>) {
        super({ fillRow: true, fillHeight: true, ...props });
    }

    protected get layoutProvidedProps(): Partial<LayoutProvidedPreviewDecoratorProps> {
        return this.layout.taskDecoratorProps;
    }

    override setupPanels(_props: Partial<DecoratorProps>) {
        this.createLeftPanel();
        this.createPreviewPanel();
        this.add(this.panelLeft, this.panelPreview);
    }

    protected createLeftPanel() {
        this.panelLeft = new Panel({
            id: "reportPanelLeft",
            width: "25%",
            paddingRight: 20,
            fillHeight: true,
            rowBreak: false
        });
        this.panelLeft.add(this.createButtonPanel());
    }

    protected createButtonPanel(): Panel {
        this.panelButtons = new Panel({
            id: "reportButtonPanel",
            fillRow: true,
            borderTopWidth: 1,
            paddingTop: 20,
            marginTop: 10,
            borderTopColor: "subtle.light"
        });
        this.createResetFormButton();
        this.createGenerateReportButton();
        this.addPanelButtonComponents();
        return this.panelButtons;
    }

    protected addPanelButtonComponents() {
        this.panelButtons.add(
            this.buttonResetForm,
            new HorizontalSpacer(),
            this.buttonPreviewAction
        );
    }

    protected createResetFormButton() {
        this.buttonResetForm = new ResetFormButton({
            color: "primary",
            dataSource: this.layout.mainDataSource,
            _designer: this.layout._designer, // Not really necessary since we don't load the decorator in the designer
            rowBreak: false,
            width: 150
        });
        this.buttonResetForm.doAfterReset = () => {
            this.layoutProvidedProps.doOnResetForm?.();
        }
    }

    public resetDefaults() {
        this.buttonResetForm.resetDefaults();
    }

    protected actionButtonsDoAfterLayoutLoaded() {
        // The reset/preview action buttons don't reside in the layout, so _doAfterOutermostLayoutLoaded() will not be
        // fired by Layout.ts.  So fire it here for those components.
        this.buttonResetForm._doAfterOutermostLayoutLoaded();
        this.buttonPreviewAction._doAfterOutermostLayoutLoaded();
    }

    private createGenerateReportButton() {
        this.buttonPreviewAction = new MultiButton({
            id: "buttonPreviewAction",
            backgroundColor: "primary",
            color: "default.reverse",
            rowBreak: false,
            width: 150
        });
        this.buttonPreviewAction.primaryButton.setProps({
            caption: "Generate Report",
            default: true,
            onClick: () => this.primaryButtonOnClick()
        });
    }

    protected primaryButtonOnClick() {
        this.buttonPreviewAction.dataSource?.executeValidatedSearch(true, (error: ErrorParam) => this.errorHandler(error, true));
    }

    protected errorHandler(error: ErrorParam, _fromSearch: boolean){
        this.panelPreview.endSearch([]);
        ReflectiveDialogs.showError(error);
    }

    protected createPreviewPanel() {
        const props = { ...this.layoutProvidedProps };
        props.reportPreviewTitle ??= this.layout.title;
        this.panelPreview = new ReportPreview(props, { width: "75%" });
    }

    override addLayout() {
        this.layout.setProps({
            fillHeight: true,
            fillRow: true,
            scrollY: true
        });
        this.panelLeft.insert(this.layout, 0);
    }

    override layoutLoaded() {
        if (StringUtil.isEmptyString(this.layout.id) === true)
            this.layout.id = "layoutReportParams";
        if (this.layout.padding === 0)
            this.layout.padding = 4;
        this.validateLayoutDataSource();
        this.configureSearchButton();
        this.buttonResetForm.dataSource = this.layout.mainDataSource;
        this.buttonPreviewAction.busyWhenDataSourceBusy = true;
        this.buttonPreviewAction.dataSource = this.layout.mainDataSource;
        this.layout.mainDataSource.addBeforeExecutionListener((event: DataSourceExecutionEvent) => this.mainDataSourceDoBeforeExecution(event));
        this.layout.mainDataSource.addAfterExecutionListener((event: DataSourceExecutionEvent) => this.mainDataSourceDoAfterExecution(event));
        this.actionButtonsDoAfterLayoutLoaded();
    }

    protected configureSearchButton() {
        this.buttonPreviewAction.dropdownItems = this.layoutProvidedProps.searchButtonAddlActions;
    }

    private validateLayoutDataSource() {
        if (this.layout.mainDataSource == null) {
            log.debug("The parameter layout needs to specify its main DataSource (that will be used when searching).");
            throw new Error("The parameter layout is not properly configured for report generation.")
        }
    }

    protected mainDataSourceDoBeforeExecution(event: DataSourceExecutionEvent){
        if (event.getAction() === DataSourceAction.SEARCH) {
            this.layoutProvidedProps.doBeforeSearch?.();
            this.panelPreview.beginSearch();
        }
    }

    protected mainDataSourceDoAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() === DataSourceAction.SEARCH) {
            const modelRow = event.dataSource.data?.[0];
            let resolvedData: ReportPreviewTabSource[];
            if (this.layoutProvidedProps.adjustReportPreviewDataAfterSearch) {
                resolvedData = new ReportPreviewDataResolver().resolve(modelRow);
                this.layoutProvidedProps.adjustReportPreviewDataAfterSearch(resolvedData, event.dataSource.data);
            }
            this.panelPreview.endSearch(resolvedData ?? modelRow);
            this.layoutProvidedProps.doAfterSearch?.(this.panelPreview.data, event.dataSource.data);
        }
    }
}
